<template>
  <div class="__CheckBoxGroup">
    <CTitle class="mb-xs">
      {{ title }}
    </CTitle>

    <div class="__CheckBoxGroup-Controller">
      <!-- Search Filter -->
      <CSearchBox
        class="mb-xs"
        v-model="filter"
        :placeholder="searchPlaceholder"
        :disabled="disabled"
        v-if="filterable"
      />

      <CMenu ref="menu">
        <template v-slot:activator>
          <CButton icon="more_vert" round />
        </template>

        <template v-slot:content>
          <CList dense>
            <CItem tag="div" clickable @click="onSelectAll">
              <CItemSection>
                <CItemLabel>
                  {{ $t('Select all') }}
                </CItemLabel>
              </CItemSection>
            </CItem>

            <CItem tag="div" clickable @click="onDeselectAll">
              <CItemSection>
                <CItemLabel>
                  {{ $t('Deselect all') }}
                </CItemLabel>
              </CItemSection>
            </CItem>
          </CList>
        </template>
      </CMenu>
    </div>

    <div class="__CheckBoxGroup-List">
      <q-list dense>
        <div class="pa-md" v-if="currentOptions.length === 0">
          {{ $t('No data.') }}
        </div>

        <q-item tag="label" :disable="disabled" v-for="(val, i) in currentOptions" :key="i" v-else>
          <q-item-section side>
            <q-checkbox
              v-model="currentValue"
              :val="val[seriesId]"
              dense
              @input="onChange(val, $event)"
            />
          </q-item-section>

          <q-item-section>
            <q-item-label lines="1">
              <CTooltip :message="val[seriesLabel]">
                {{ val[seriesLabel] }}
              </CTooltip>
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>

      <!-- Loading -->
      <CLoading v-model="loading" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { filterObjFn } from '@/lib/filterFn';
import CTitle from '@/components/uikit/CTitle';
import CSearchBox from '@/components/uikit/CSearchBox';
import CheckBox from '@/components/uikit/CCheckBox';
import CLoading from '@/components/uikit/CLoading';
import CButton from '@/components/uikit/CButton';
import CRadioButton from '@/components/uikit/CRadioButton';
import CMenu from '@/components/uikit/CMenu';
import CList from '@/components/uikit/CList';
import CItem from '@/components/uikit/CItem';
import CItemSection from '@/components/uikit/CItemSection';
import CItemLabel from '@/components/uikit/CItemLabel';
import CTooltip from '@/components/uikit/CTooltip';
import CPopupInfo from '@/components/uikit/CPopupInfo';
import DropdownMenu from '@/components/DropdownMenu';
const messages = {
  en: {
    'It is sorted in descending order of usage fee.':
      'It is sorted in descending order of usage fee.',
    'Search by {label} name.': 'Search by {label} name.',
    'No data.': 'No data.',
    'No results.': 'No results.',
    'Select all': 'Select All',
    'Deselect all': 'Unselect All',
  },
  ja: {
    'It is sorted in descending order of usage fee.': '利用料の多い順でソートされています。',
    'Search by {label} name.': '{label}名で検索',
    'No data.': 'データがありません。',
    'No results.': '検索結果がありません。',
    'Select all': 'すべてを選択',
    'Deselect all': 'すべての選択を解除',
  },
};
export default Vue.extend({
  name: 'FilterOptions',
  i18n: { messages },
  components: {
    CTitle,
    CheckBox,
    CSearchBox,
    CLoading,
    CButton,
    CRadioButton,
    CMenu,
    CList,
    CItem,
    CItemSection,
    CItemLabel,
    CTooltip,
    CPopupInfo,
    DropdownMenu,
  },
  props: {
    value: {
      type: [Array, String],
    },
    title: {
      type: String,
    },
    label: {
      type: String,
    },
    series: {
      type: Array,
      default: () => {
        return [];
      },
      required: true,
    },
    seriesLabel: {
      type: String,
      default: 'label',
    },
    seriesId: {
      type: String,
      default: 'id',
    },
    seriesValue: {
      type: String,
      default: 'value',
    },
    optionDescription: {
      type: String,
      default: 'description',
    },
    loading: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    // Search Filter
    filterable: {
      type: Boolean,
    },
    filterKey: {
      type: Array,
      default: () => {
        return ['label'];
      },
    },
    searchPlaceholder: {
      type: String,
    },
  },
  data() {
    return {
      filter: '',
    };
  },
  computed: {
    // Values / Options
    currentValue: {
      set(val) {
        this.$emit('input', val);
      },
      get() {
        return this.value;
      },
    },
    currentOptions() {
      let options = this.series;
      // Search Filter
      if (this.filterable) {
        options = filterObjFn(options, this.filter, this.filterKey);
      }
      return options;
    },
  },
  methods: {
    onChange(val, evt) {
      const id = val.id;
      const isExist = evt.includes(id);
      this.$emit('change', { id: id, val: isExist });
    },
    onSelectAll() {
      const selectedList = [];
      for (const option of this.currentOptions) {
        selectedList.push(option[this.seriesId]);
      }
      Vue.set(this, 'currentValue', selectedList);
    },
    onDeselectAll() {
      Vue.set(this, 'currentValue', []);
    },
  },
});
</script>

<style lang="scss" scoped>
.q-list--dense::v-deep > .q-item {
  padding-left: $space-sm;
  padding-right: $space-sm;
}
.q-list::v-deep {
  label {
    display: flex;
  }
}
.__CheckBoxGroup {
  &-Controller {
    display: flex;
  }
  &-List {
    position: relative;
    overflow-y: auto;
    height: 350px;
  }
}
</style>
